import {
  ClipboardText,
  Detective,
  Function as FunctionIcon,
  Garage,
  HouseLine,
  Phone,
  Receipt,
} from '@phosphor-icons/react';
import type { IconComponent } from '@relay-towing/ui';
import {
  FUNCTION_ITEMS,
  OPERATION_ITEMS,
  type MobileTabId,
  type TabItem,
} from '../../lib/driverAppTabs';

/**
 * Each tab's word, icon and the path it opens. The icons are the Figma's
 * tab bar: a house, a phone, the ƒ for Functions and the detective for
 * Operations. The tabs someone who only drives has in place of Functions
 * are its items, with the items' own words, icons and paths.
 */
export const TAB_DETAILS: Record<
  MobileTabId,
  { label: string; icon: IconComponent; path: string }
> = {
  home: { label: 'Home', icon: HouseLine, path: '/' },
  calls: { label: 'Calls', icon: Phone, path: '/calls' },
  invoices: { label: 'Invoices', icon: Receipt, path: '/invoices' },
  impounds: { label: 'Impounds', icon: Garage, path: '/impounds' },
  inspections: {
    label: 'Inspections',
    icon: ClipboardText,
    path: '/inspections',
  },
  functions: { label: 'Functions', icon: FunctionIcon, path: '/functions' },
  operations: { label: 'Operations', icon: Detective, path: '/operations' },
};

const opensAt = (items: readonly TabItem[], pathname: string) =>
  items.some(
    (item) => 'screen' in item.opens && item.opens.screen === pathname
  );

/**
 * The member's tab a path belongs to: one of their tabs' own paths, or a
 * list opened from Functions or Operations, so Functions stays lit on
 * /invoices for the office while Invoices is lit there for someone who
 * only drives. Null for a screen opened from a list (a call, an invoice),
 * which shows no tab bar.
 */
export function tabForPath(
  pathname: string,
  tabs: readonly MobileTabId[]
): MobileTabId | null {
  const own = tabs.find((id) => TAB_DETAILS[id].path === pathname);
  if (own) return own;
  if (opensAt(FUNCTION_ITEMS, pathname)) return 'functions';
  if (opensAt(OPERATION_ITEMS, pathname)) return 'operations';
  return null;
}
